import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMemo } from "react";

export const MoodTracker = ({ Score }) => {
  const EmojiMap = {
    terrible: { value: "😩", score: 0 },
    bad: { value: "☹️", score: 1 },
    okay: { value: "🙂", score: 2 },
    good: { value: "😊", score: 3 },
    excellent: { value: "😃", score: 4 },
  };

  const yValueToMood = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(EmojiMap).map(([mood, details]) => [details.score, mood])
      ),
    []
  );

  // console.log(yValueToMood);

  const yAxisTicks = useMemo(
    () => Object.values(EmojiMap).map((details) => details.score),
    []
  );

  // console.log(yAxisTicks);

  const chartData = useMemo(() => {
    return Score.map((item) => ({
      date: item.date,
      mood: item.value,
      yValue: EmojiMap[item.value]?.score, // Use EmojiMap to get the Y value
    }));
  }, [Score]);

  // Month abbrev formatter for X-axis and inline small grid labels
  const monthAbbrev = {
    January: 'Jan', February: 'Feb', March: 'Mar', April: 'Apr', May: 'May', June: 'Jun',
    July: 'Jul', August: 'Aug', September: 'Sep', October: 'Oct', November: 'Nov', December: 'Dec'
  };
  const formatMonthShort = (label) => {
    if (typeof label !== 'string') return label;
    const [maybeMonth, ...rest] = label.split(' ');
    const short = monthAbbrev[maybeMonth] || maybeMonth;
    return [short, ...rest].join(' ');
  };

  const xAxisProps = {};
  if (chartData.length > 4) {
    xAxisProps.angle = -45;
    xAxisProps.textAnchor = "end";
    xAxisProps.height = 60;
  }

  return (
    <div className="bg-white w-8/12 rounded-2xl p-4">
      {/* <p className="font-bold text-lg text-left p-2">Mood chart</p> */}

      {Score.length <= 7 ? (
        <>
          <div className="grid grid-cols-7 gap-2">
            {Score.map((item) => {
              return (
                <div
                  key={`emoji-${item.value}+${item.date}`}
                  className="text-3xl"
                >
                  {EmojiMap[item.value]?.value || ""}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Score.map((item) => {
              return (
                <div key={`date-${item.date}+${item.date}`}>{formatMonthShort(item.date)}</div>
              );
            })}
          </div>
        </>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            // ADJUST THE LEFT MARGIN HERE to create space for the Y-axis
            margin={{ top: 20, right: 30, left: 20, bottom: xAxisProps.height || 5 }}
          >
            <XAxis
              axisLine={false}
              tickLine={false}
              padding={{left: 30}}
              dataKey="date"
              interval="preserveStartEnd" // Ensures the first and last ticks are always visible
              tickFormatter={formatMonthShort}
              {...xAxisProps}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[-0.5, 4.5]}
              ticks={yAxisTicks}
              tickFormatter={(value) => {
                const mood = yValueToMood[value];
                return EmojiMap[mood]?.value;
              }}
              tick={{ fontSize: 24 }}
              tickMargin={10}
            />
            <Line
              connectNulls
              dot={false}
              type="monotone"
              dataKey="yValue"
              stroke="#64AEE2"
              strokeWidth={2}
              activeDot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
